"use client";

// SiteMenu — the ONE site-wide crumb + menu, composed from the canonical parts
// so every page behaves identically to the dashboard and the landing:
//
//   • the CRUMB is BrandCrumb — the dashboard lockup (wordmark at
//     WORDMARK_INLINE_EM, --t-crumb, baseline law, descender-safe line box) with
//     the ≡ riding right after the wordmark (afterRoot), and an optional inline
//     search affordance that fades away while the menu owns the screen.
//   • the MENU is OsMenu — the dashboard's full page-view takeover (frosted veil
//     z-40 · netti z-44 · the big left-aligned list z-48, swipable spaces on
//     desktop AND mobile, identical timings).
//   • the FOOTER is the landing's menu footer (social · docs/security/contact ·
//     the Product Hunt badge) — it pops up with the menu no matter where you are.
//
// The host page raises netti to centre stage on open via `onOpenChange` — apply
// the MENU_ORG values below to the page's OrganismBackdrop so the choreography
// (bottom-centre, -6vh, scale, 760ms, above the veil) is the same everywhere.

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import BrandCrumb, { type Crumb } from "./BrandCrumb";
import OsMenu from "./os/OsMenu";
import EnterPill from "./EnterPill";
import { openCreateYours, type CreateYoursIntent } from "./onboarding/CreateYours";
import UserChip, { type MeCard } from "./UserChip";
import { buildMenuTree, type MenuSpace } from "../lib/nav/siteNav";

export type SiteCrumbSeg = Crumb;

/** the canonical menu-open organism choreography (mirrors OsShell's menuOrg) —
 *  spread these onto the page's OrganismBackdrop while the menu is open. */
export const MENU_ORG = {
  anchor: "bottom" as const,
  offset: { y: "-6vh" },
  glide: true,
  glideAxis: "both" as const,
  opacity: 0.94,
  zIndex: 44,
  innerTransMs: 760,
  innerEase: "var(--ease-soft)",
};
export const MENU_ORG_SCALE = { wide: 1.24, narrow: 1.02 };

export default function SiteMenu({
  trail = [],
  founder = false,
  mode = "crumb",
  current,
  hereKey,
  search,
  signedIn = false,
  enter = true,
  onOpenChange,
  userPreview,
  startKey,
  ctaLabel = "create yours",
  ctaIntent,
}: {
  /** breadcrumb after the wordmark, e.g. [{label:"company",href:"/company"}, {label:"blog"}] */
  trail?: SiteCrumbSeg[];
  founder?: boolean;
  /** "crumb" — the fixed dashboard-geometry header (company, standalone pages).
   *  "button" — just the ≡ + takeover, inline in another crumb's afterRoot (docs). */
  mode?: "crumb" | "button";
  /** THIS page's own sections — the first menu space (label per surface). */
  current: MenuSpace;
  /** dot the matching innernet page (e.g. "company"). */
  hereKey?: string;
  /** inline search affordance rendered after the trail (the os-crumb-search
   *  grammar) — fades away while the menu is open. */
  search?: React.ReactNode;
  /** inside the app already — surfaces "yours" in the tree; hides the enter pill. */
  signedIn?: boolean;
  /** show the top-right "enter" door into the app (crumb mode only). */
  enter?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** stand in for the user-card fetch (the dev chrome lab). Never set in the product. */
  userPreview?: MeCard;
  /** open the takeover drilled into this space (the deck passes its own id, so
   *  the ≡ is that surface's contents page; the parent site is one step up). */
  startKey?: string;
  /** what the signed-out door SAYS here. The commons offers skills, so there
   *  the honest verb is "save it", not "create yours". */
  ctaLabel?: string;
  /** why it was opened, so the modal can answer that reason (CreateYours). */
  ctaIntent?: CreateYoursIntent;
}) {
  const [open, setOpen] = useState(false);
  // the ≡ takeover is the ONE takeover on a page — the you-chip is a door, not a
  // second menu — so it alone owns the screen while it is up.
  const owned = open;
  const router = useRouter();

  useEffect(() => { onOpenChange?.(owned); }, [owned, onOpenChange]);

  // WARM THE ROOMS WHILE THE MENU IS OPEN. Every public room is force-dynamic,
  // so a press used to start its render from cold: the row lit, the menu
  // collapsed, and the new room landed a second later. The menu is open for as
  // long as it takes to read it — that time is free, so the payload for every
  // door in it is already in flight before a finger lands on one. (Prefetch is
  // idempotent and cheap; Next dedupes and caches it for the tab's lifetime.)
  useEffect(() => {
    if (!open) return;
    for (const href of ["/company", "/community", "/docs", "/"]) router.prefetch(href);
  }, [open, router]);

  // the ONE truth: the breadcrumb tree rooted at innernet.
  const { root, startPath } = useMemo(
    () => buildMenuTree({
      current,
      founder,
      signedIn,
      hereKey,
      startKey,
      nav: (href) => router.push(href),
      close: () => setOpen(false),
    }),
    [current, founder, signedIn, hereKey, startKey, router],
  );

  const menuBtn = (
    <button
      type="button"
      className="tlm-btn"
      aria-label={open ? "close menu" : "menu"}
      aria-expanded={open}
      data-noswipe
      style={{ flexShrink: 0 }}
      onClick={() => setOpen((o) => !o)}
    >
      {/* stroke 2.28, not 2.4 — 5% finer, so the mark's weight matches the
          wordmark's letterforms beside it instead of reading a shade bolder. */}
      <svg className="tlm-btn__ic" viewBox="0 0 44 30" aria-hidden>
        <rect x="1.5" y="1.5" width="41" height="27" rx="13.5" fill="none" stroke="currentColor" strokeWidth="2.28" />
        <line className="tlm-btn__l1" x1="15" y1="12" x2="29" y2="12" stroke="currentColor" strokeWidth="2.28" strokeLinecap="round" />
        <line className="tlm-btn__l2" x1="15" y1="18" x2="29" y2="18" stroke="currentColor" strokeWidth="2.28" strokeLinecap="round" />
      </svg>
    </button>
  );

  // OsMenu renders the veil, breadcrumb tree, netti, AND the full MenuFooter.
  const takeover = <OsMenu open={open} root={root} startPath={startPath} onClose={() => setOpen(false)} />;

  if (mode === "button") {
    return (
      <>
        {menuBtn}
        {takeover}
      </>
    );
  }

  return (
    <>
      {/* the fixed header — the exact dashboard/docs geometry: 1.4rem band, the
          shell max-width + gutter, the crumb at --t-crumb. Above the veil while
          the menu breathes (only the lockup + ≡ stay interactive). */}
      <header
        style={{
          position: "fixed", top: 0, left: 0, right: 0,
          padding: "1.4rem 0",
          zIndex: owned ? 60 : 30,
          // law_mapped_controls: the band's padding is empty air. It used to
          // take the pointer, and 23px of it hung over the ticker below, so the
          // top half of "guides" belonged to a bar you could not see. Only the
          // visible line answers.
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            pointerEvents: "auto",
            maxWidth: "var(--os-shell-max)", margin: "0 auto", paddingInline: "var(--os-gutter)",
            // the top bar is ONE centred line: the wordmark lockup, the search,
            // and the enter door all share a vertical centre — so the button's
            // label lands on the same centre-line as the wordmark, never riding
            // high or low (the crumb keeps its own baseline law internally).
            display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0, fontSize: "var(--t-crumb)",
          }}
        >
          <span style={{ display: "inline-flex", minWidth: 0, flex: "0 1 auto" }}>
            <BrandCrumb
              segments={trail}
              size="inherit"
              rootHref="/"
              afterRoot={menuBtn}
              style={{ flex: "0 1 auto", minWidth: 0 }}
            />
          </span>
          {search && (
            <span
              className="site-crumb-search"
              style={{
                display: "inline-flex", minWidth: 0,
                opacity: owned ? 0 : 1,
                pointerEvents: owned ? "none" : undefined,
                transition: "opacity 0.4s var(--ease-soft)",
              }}
            >
              {search}
            </span>
          )}
          {/* Top-right is ONE slot with two states: a stranger gets the door in,
              a signed-in person gets themselves — and pressing yourself takes
              you home to your innernet, the same on every surface. UserChip
              decides on its own — it asks /api/me/card — so a statically
              rendered page (docs, a skill) still shows you without being forced
              dynamic, and `signedIn` stays a hint for the menu tree rather than
              a second source of truth the chrome could disagree with. */}
          <span
            style={{
              marginLeft: "auto", alignSelf: "center", flexShrink: 0,
              // while the takeover is up it owns the screen: the right-side
              // chrome fades with everything else (navigator §1), the chip too.
              opacity: owned ? 0 : 1,
              pointerEvents: owned ? "none" : undefined,
              transition: "opacity 0.4s var(--ease-soft)",
            }}
          >
            <UserChip
              preview={userPreview}
              fallback={
                enter ? (
                  <EnterPill
                    label={ctaLabel}
                    ariaLabel={ctaLabel === "save it" ? "save this to your innernet" : "create your innernet"}
                    onClick={() => openCreateYours(ctaIntent)}
                  />
                ) : null
              }
            />
          </span>
        </div>
      </header>
      {takeover}
    </>
  );
}
